import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function CloverSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="Clover Setup"
        subtitle="What you need to configure inside CLOVER POS so it can send orders correctly."
        logo="/static/wiki/clover/clover_logo.png"
        backPath="/help-center/pos-configuration"
        backLabel="POS Configuration Guide"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>This document provides a client-ready step-by-step process for:</p>
          <ul className="wiki-list">
            <li>Connecting Clover to your Stream-Editor location</li>
            <li>Installing the integration app on <strong>Clover POS Station</strong></li>
            <li>Linking Clover POS with the OCB Desktop App</li>
          </ul>

          <h3>Requirements</h3>
          <ul className="wiki-list">
            <li>Access to Stream-Editor (Your company&apos;s environment and login);</li>
            <li>Access to <strong>Clover Dashboard</strong> (Live merchant account);</li>
            <li>Company&apos;s Clover POS station (With App Market enabled);</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="stream-editor" title="A - SET UP CLOVER IN STREAM-EDITOR">
        <Article>
          <h3>Log In to Stream-Editor</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open your browser, navigate to your <strong>Stream-Editor URL</strong>, and log in with your credentials.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click at <strong>OCB Settings.</strong></p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Under <strong>Locations</strong>, choose a location or create a new one and click on the <strong>⚙️ gear icon</strong> next to the location to open the Integrations panel.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/clover/clover-a1.png" alt="OCB Settings navigation" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/clover/clover-a2.png" alt="Location settings" className="wiki-screenshot" />
          </div>

          <h3>Connect Clover</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>From the the Integrations panel list, select <strong>CLOVER</strong> and click on <strong>Connect to Clover</strong> button (You will be redirected to <strong>Clover&apos;s login page</strong>)</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/clover/clover-a3.png" alt="Select Clover integration" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/clover/clover-a4.png" alt="Connect to Clover dialog" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Enter your account credentials and click the <strong>Log In</strong> button.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-a5.png" alt="Clover login page" className="wiki-screenshot" />
          </div>

          <h3>Choose your Merchant (if Applicable)</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>If your account has access to <strong>more than one</strong> Merchant, you&apos;ll be asked to select the one you want to connect.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>If there is only one, the system will automatically proceed.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-a6.png" alt="Select merchant screen" className="wiki-screenshot" />
          </div>

          <h3>Grant Access</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After selecting your merchant (if applicable), you will be taken to the Clover App Authorization screen</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click <strong>Connect</strong> to grant permission for the Stream Editor to sync your data and confirm the <strong>Pricing &amp; Subscription Information</strong> modal.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/clover/clover-a7.png" alt="Clover app authorization" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/clover/clover-a8.png" alt="Pricing and subscription modal" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>A <em>&quot;Please return to Stream Editor and start the integration process again&quot;</em> message will appear.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-a9.png" alt="Return to Stream Editor message" className="wiki-screenshot" />
          </div>

          <h3>Start Over the Clover Connection Process</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Return to <strong>Stream-Editor</strong>, reopen the <strong>Clover integration</strong>, and click <strong>Connect to Clover</strong> again;</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/clover/clover-a10.png" alt="Reopen Clover integration" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/clover/clover-a11.png" alt="Connect to Clover again" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once redirected to <strong>Clover&apos;s page</strong> again, you&apos;ll need to select your Merchant one more time;</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-a12.png" alt="Select merchant again" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After clicking in your merchant, you should return to <strong>Stream-Editor</strong> with a confirmation that Clover is now integrated</p>
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="pos-station" title="B - SET UP CLOVER POS STATION - OCB APP">
        <Article>
          <h3>Install the OCB App</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Launch <strong>OCB App</strong> to make sure it opens without errors.</p>
          </div>

          <h3>Configure Clover POS Station</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In the <strong>POS Station</strong>, open the <strong>App Market</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select <strong>My Apps</strong>.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/clover/clover-b1.png" alt="App Market icon" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/clover/clover-b2.png" alt="My Apps screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Find the name of your integrated app (In this case <strong>Stream</strong>), and click <strong>Download</strong> to install it.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/clover/clover-b3.png" alt="My Apps list with Stream" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/clover/clover-b4.png" alt="Stream app download page" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once installed, locate the app icon in the <strong>POS Station</strong> menu and open it.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-b5.png" alt="Stream Order Confirmation app icon" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="confirm-settings" title="C - CONFIRM OCB APP SETTINGS ON POS STATION">
        <Article>
          <h3>Review Configuration Settings</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open <strong>Stream Order Confirmation</strong> app at your <strong>POS Station</strong></p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on Settings and confirm all setup configurations.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-c1.png" alt="Stream Order Confirmation main screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-c2.png" alt="Configuration settings" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-c3.png" alt="Note about App ID setup" className="wiki-screenshot" />
          </div>

          <h3>Creating an Order</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on <strong>Register</strong> to open the <strong>Register Interface</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-c4.png" alt="Register interface" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Create a new order (add items, set quantity, complete transaction).</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/clover/clover-c5.png" alt="Creating an order" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default CloverSetup
